/**
 * The icon and accent a notification is filed under, by category.
 *
 * The colours are `--elx-cat-*` custom properties, declared per scheme in
 * `resources/sass/global.scss`. They used to be hex literals written here, which
 * meant they were colours dark mode could not reach — and no gate caught it,
 * because the rule scans `<style>` blocks and these were hiding in a `.js`.
 *
 * Six families for fifteen categories, on purpose: the colour says which corner
 * of the app a line came from, the icon says which thing inside it. Filing
 * quotes, orders and invoices under three different hues would only make three
 * shades of "sales" nobody can tell apart.
 *
 * Kept apart from presenter.js so it can be tested without a browser: the
 * presenter pulls in the i18n filters, this file pulls in nothing.
 */
export const CATEGORY_STYLE = {
    mentions: { icon: 'mdi-at', color: 'var(--elx-cat-purple)' },
    tasks: { icon: 'mdi-checkbox-marked-outline', color: 'var(--elx-cat-green)' },
    reminders: { icon: 'mdi-clock-alert-outline', color: 'var(--elx-cat-orange)' },
    contacts: { icon: 'mdi-account-outline', color: 'var(--elx-cat-blue)' },
    opportunities: { icon: 'mdi-trending-up', color: 'var(--elx-cat-purple)' },
    invoices: { icon: 'mdi-file-document-outline', color: 'var(--elx-cat-red)' },
    quotes: { icon: 'mdi-file-document-edit-outline', color: 'var(--elx-cat-blue)' },
    orders: { icon: 'mdi-cart-outline', color: 'var(--elx-cat-blue)' },
    contracts: { icon: 'mdi-file-sign', color: 'var(--elx-cat-green)' },
    calendar: { icon: 'mdi-calendar', color: 'var(--elx-cat-purple)' },
    document_requests: { icon: 'mdi-file-upload-outline', color: 'var(--elx-cat-teal)' },
    tickets: { icon: 'mdi-face-agent', color: 'var(--elx-cat-blue)' },
    mailboxes: { icon: 'mdi-email-outline', color: 'var(--elx-cat-blue)' },
    integrations: { icon: 'mdi-sync', color: 'var(--elx-cat-green)' },
    sso: { icon: 'mdi-shield-account-outline', color: 'var(--elx-cat-blue)' },
    // Someone shared a saved search with you — the same "a person did something
    // that involves you" family as a mention, and it had no entry at all until
    // the test below went looking for one.
    saved_views: { icon: 'mdi-bookmark-outline', color: 'var(--elx-cat-purple)' },
    // Consumption against a cap or a balance — the same "something is about to
    // stop working" family as a reminder, which is why it shares its hue.
    usage: { icon: 'mdi-gauge', color: 'var(--elx-cat-orange)' },
    // A dossier filed with an outside authority, waited on, answered — the same
    // family as a document request, which is why it shares its hue.
    compliance: { icon: 'mdi-shield-check-outline', color: 'var(--elx-cat-teal)' },
    // Hours approved or sent back. Green like tasks and contracts: something you
    // did has been looked at, rather than something being asked of a customer.
    timesheets: { icon: 'mdi-clock-check-outline', color: 'var(--elx-cat-green)' }
};

/**
 * What an unrecognised category falls back to. A generic bell is the honest
 * answer — registering the category is the fix, and the test that walks the
 * registry is what says when one is missing.
 */
export const DEFAULT_STYLE = { icon: 'mdi-bell-outline', color: 'var(--elx-cat-blue)' };

export function categoryStyle(category) {
    return CATEGORY_STYLE[category] || DEFAULT_STYLE;
}
